'use client';

import { useMemo, useState } from 'react';

import {
    Align,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Icon,
    IconName,
    IconSize,
    Spacer,
    SpacerSize,
} from '@bytel/trilogy-react';

import { getSessionVariable } from '@services/storage';

import { ShortListContext } from '@app-types/eligibility';
import { StoredHomepackProduct } from '@app-types/home-pack';
import { CategoryCodeInfo } from '@app-types/plan';

import { RocCard } from '@components/roc-card';

import { ELIGIBILITY_STORAGE_KEY, TECHNOLOGY } from '@constants/provider';

import {
    EligibilityOffer,
    EligibilityResults,
    JourneyConfigType,
    filterOut4GWhen5GExists,
    sortOffersWithCategoryOrder,
} from '@eligibility/helpers';

import { SelectedPackProductCard } from '../selected-pack-product-card';

interface OfferListProps {
    currentTab?: { name: TECHNOLOGY; label: string };
    journeyConfig: JourneyConfigType;
    results: EligibilityResults;
    shortList?: ShortListContext;
    categoryCodeInfo: CategoryCodeInfo[];
    onAddToCart: (gencode: string) => void;
    hideSecondaryOffers?: boolean;
}

export function OfferList({
    currentTab,
    journeyConfig,
    results,
    shortList,
    categoryCodeInfo,
    onAddToCart,
    hideSecondaryOffers = false,
}: Readonly<OfferListProps>) {
    const [shouldShowSecondaryOffers, setShouldShowSecondaryOffers] = useState<boolean>(false);

    const selectedPackProductSessionVariable = getSessionVariable(ELIGIBILITY_STORAGE_KEY.PACK_PRODUCT);
    const selectedPackProduct = selectedPackProductSessionVariable
        ? (JSON.parse(selectedPackProductSessionVariable) as StoredHomepackProduct)
        : null;

    const [primaryOffers, secondaryOffers, hasPrimaryOfferHat, hasSecondaryOffersHat] = useMemo(() => {
        if (!currentTab) {
            return [[], []];
        }
        if (!shortList || currentTab.name !== shortList.technology.toUpperCase()) {
            const offers = results.offers[currentTab.name]!.filter((offer) => !offer.isHidden);
            const filteredOffers = filterOut4GWhen5GExists(offers);

            return [
                sortOffersWithCategoryOrder(categoryCodeInfo, filteredOffers),
                [],
                filteredOffers.some((offer) => offer.hat !== undefined),
                false,
            ];
        }

        let primaryOffers: EligibilityOffer[] = [];
        let secondaryOffers: EligibilityOffer[] = [];
        let hasPrimaryOfferHat: boolean = false;
        let hasSecondaryOffersHat: boolean = false;

        const allOffers = Object.values(results.offers).flat();

        allOffers.forEach((offer) => {
            if (offer.isSameRangeOrOldRange(shortList.rangeNgCommercialSeries)) {
                if (!offer.isHiddenInFocus) {
                    primaryOffers.push(offer);

                    if (offer.hat) {
                        hasPrimaryOfferHat = true;
                    }
                }
            } else if (!offer.isHidden) {
                secondaryOffers.push(offer);

                if (offer.hat) {
                    hasSecondaryOffersHat = true;
                }
            }
        });

        if (primaryOffers.length === 0 && secondaryOffers.length > 0) {
            primaryOffers = sortOffersWithCategoryOrder(categoryCodeInfo, secondaryOffers);
            secondaryOffers = [];
        } else {
            secondaryOffers = sortOffersWithCategoryOrder(categoryCodeInfo, secondaryOffers);
        }

        primaryOffers = filterOut4GWhen5GExists(primaryOffers);
        secondaryOffers = filterOut4GWhen5GExists(secondaryOffers);
        hasPrimaryOfferHat = primaryOffers.some((offer) => offer.hat !== undefined);
        hasSecondaryOffersHat = secondaryOffers.some((offer) => offer.hat !== undefined);

        return [primaryOffers, secondaryOffers, hasPrimaryOfferHat, hasSecondaryOffersHat];
    }, [results, currentTab, shortList]);

    if (!currentTab) {
        return null;
    }

    return (
        <>
            <Spacer size={SpacerSize.FOUR} />
            <Columns align={Align.CENTER} gap={2} multiline>
                {shortList?.focus &&
                    journeyConfig.showComboProductSlug &&
                    selectedPackProduct &&
                    journeyConfig.showComboProductSlug.includes(selectedPackProduct.slug) && (
                        <>
                            <Column size={5}>
                                <SelectedPackProductCard selectedProduct={selectedPackProduct} />
                            </Column>
                            <Column className='has-text-centered m-auto'>
                                <Icon name={IconName.PLUS_CIRCLE} size={IconSize.LARGE} />
                            </Column>
                        </>
                    )}
                {primaryOffers.map((offer) => (
                    <Column
                        key={offer.sku}
                        mobileSize={12}
                        tabletSize={6}
                        desktopSize={4}
                        data-cy={`${offer.rangeNg}_${offer.commercialSeries}`}
                    >
                        <RocCard
                            plan={offer.mapToFaiType()}
                            cta={{
                                onClick: () => onAddToCart(offer.sku),
                                text: 'Ajouter au panier',
                                color: ButtonVariant.CONVERSION,
                            }}
                            hasSiblingHat={hasPrimaryOfferHat}
                            showComparatorCheckbox={journeyConfig.showComparatorSelection}
                        />
                    </Column>
                ))}
            </Columns>

            {!hideSecondaryOffers && secondaryOffers.length > 0 && (
                <>
                    <Spacer size={SpacerSize.EIGHT} />
                    <Columns marginless>
                        <Column className='has-text-centered'>
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                iconName={shouldShowSecondaryOffers ? IconName.ARROW_UP : IconName.ARROW_DOWN}
                                onClick={() => setShouldShowSecondaryOffers((oldValue) => !oldValue)}
                            >
                                {shouldShowSecondaryOffers ? 'Masquer les offres' : 'Afficher les autres offres'}
                            </Button>
                        </Column>
                    </Columns>
                    {shouldShowSecondaryOffers && (
                        <>
                            <Spacer size={SpacerSize.SIX} />
                            <Columns multiline align={Align.CENTER} gap={2}>
                                {secondaryOffers.map((offer) => (
                                    <Column
                                        key={offer.sku}
                                        mobileSize={12}
                                        tabletSize={6}
                                        desktopSize={4}
                                        data-cy={`${offer.rangeNg}_${offer.commercialSeries}`}
                                    >
                                        <RocCard
                                            plan={offer.mapToFaiType()}
                                            cta={{
                                                onClick: () => onAddToCart(offer.sku),
                                                text: 'Ajouter au panier',
                                                color: ButtonVariant.CONVERSION,
                                            }}
                                            hasSiblingHat={hasSecondaryOffersHat}
                                            showComparatorCheckbox={journeyConfig.showComparatorSelection}
                                        />
                                    </Column>
                                ))}
                            </Columns>
                        </>
                    )}
                </>
            )}
        </>
    );
}
